import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Check, X, Edit2, Target, ListChecks, Zap, MessageSquare, TrendingUp,
  ChevronRight, AlertTriangle, Sparkles, Loader2, Save
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Type icons mapping
const TYPE_ICONS = {
  todo: ListChecks,
  rock: Target,
  issue: Zap,
  headline: MessageSquare,
  scorecard: TrendingUp
};

// Type colors
const TYPE_COLORS = {
  todo: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  rock: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  issue: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  headline: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  scorecard: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
};

// Confidence styling
const getConfidenceStyle = (score) => {
  if (score >= 0.8) return 'bg-green-600';
  if (score >= 0.6) return 'bg-yellow-600';
  return 'bg-gray-500';
};

const getConfidenceLabel = (score) => {
  if (score >= 0.8) return 'High';
  if (score >= 0.6) return 'Medium';
  return 'Low';
};

/**
 * Review & Apply Dialog - Safe AI suggestions workflow
 * Displays AI-suggested updates with approve/edit/reject actions
 */
const ReviewApplyDialog = ({
  open,
  onOpenChange,
  meetingId,
  suggestions = [],
  onSuggestionsUpdate,
  onComplete
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [processingId, setProcessingId] = useState(null);
  const [processingAll, setProcessingAll] = useState(false);

  const handleApprove = async (suggestion) => {
    setProcessingId(suggestion.id);
    try {
      await axios.post(
        `${API}/eos/meetings/${meetingId}/copilot/apply-update/${suggestion.id}`,
        { action: 'approve', edited_data: null }
      );

      // Update local state
      const updated = suggestions.map(s =>
        s.id === suggestion.id ? { ...s, status: 'approved' } : s
      );
      onSuggestionsUpdate?.(updated);
      toast.success('Update applied successfully');
    } catch (error) {
      console.error('Error applying update:', error);
      toast.error('Failed to apply update');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (suggestion) => {
    setProcessingId(suggestion.id);
    try {
      await axios.post(
        `${API}/eos/meetings/${meetingId}/copilot/apply-update/${suggestion.id}`,
        { action: 'reject', edited_data: null }
      );

      const updated = suggestions.map(s =>
        s.id === suggestion.id ? { ...s, status: 'rejected' } : s
      );
      onSuggestionsUpdate?.(updated);
      toast.info('Update rejected');
    } catch (error) {
      console.error('Error rejecting update:', error);
      toast.error('Failed to reject update');
    } finally {
      setProcessingId(null);
    }
  };

  const handleEdit = (suggestion) => {
    setEditingId(suggestion.id);
    setEditedData({
      title: suggestion.title || '',
      owner_suggestion: suggestion.owner_suggestion || '',
      ...suggestion.changes
    });
  };

  const handleSaveEdit = async (suggestion) => {
    setProcessingId(suggestion.id);
    try {
      await axios.post(
        `${API}/eos/meetings/${meetingId}/copilot/apply-update/${suggestion.id}`,
        { action: 'approve', edited_data: editedData }
      );

      const updated = suggestions.map(s =>
        s.id === suggestion.id ? { ...s, status: 'approved', ...editedData } : s
      );
      onSuggestionsUpdate?.(updated);
      setEditingId(null);
      setEditedData({});
      toast.success('Update applied with edits');
    } catch (error) {
      console.error('Error applying edited update:', error);
      toast.error('Failed to apply update');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedData({});
  };

  const handleApproveAll = async () => {
    const pendingSuggestions = suggestions.filter(s => s.status === 'pending' && s.confidence >= 0.8);
    if (pendingSuggestions.length === 0) {
      toast.info('No high-confidence suggestions to approve');
      return;
    }

    setProcessingAll(true);
    try {
      for (const suggestion of pendingSuggestions) {
        await axios.post(
          `${API}/eos/meetings/${meetingId}/copilot/apply-update/${suggestion.id}`,
          { action: 'approve', edited_data: null }
        );
      }

      const updated = suggestions.map(s =>
        pendingSuggestions.find(p => p.id === s.id)
          ? { ...s, status: 'approved' }
          : s
      );
      onSuggestionsUpdate?.(updated);
      toast.success(`Applied ${pendingSuggestions.length} high-confidence updates`);
    } catch (error) {
      console.error('Error applying all updates:', error);
      toast.error('Failed to apply all updates');
    } finally {
      setProcessingAll(false);
    }
  };

  const pendingCount = suggestions.filter(s => s.status === 'pending').length;
  const approvedCount = suggestions.filter(s => s.status === 'approved').length;
  const rejectedCount = suggestions.filter(s => s.status === 'rejected').length;
  const highConfidenceCount = suggestions.filter(s => s.status === 'pending' && s.confidence >= 0.8).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Review & Apply AI Suggestions
          </DialogTitle>
          <DialogDescription>
            Review each AI-suggested update before applying. No changes are made without your explicit approval.
          </DialogDescription>
        </DialogHeader>

        {/* Summary Stats */}
        <div className="flex items-center gap-4 py-2 px-1 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Badge variant="outline">{pendingCount} Pending</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-green-600">{approvedCount} Approved</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{rejectedCount} Rejected</Badge>
          </div>
          <div className="ml-auto">
            {highConfidenceCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleApproveAll}
                disabled={processingAll}
              >
                {processingAll ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-1" />
                )}
                Approve All High Confidence ({highConfidenceCount})
              </Button>
            )}
          </div>
        </div>

        {/* Suggestions List */}
        <ScrollArea className="flex-1 pr-4">
          {suggestions.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No AI suggestions generated yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Complete meeting sections to get AI-suggested updates
              </p>
            </div>
          ) : (
            <div className="space-y-3 pb-4">
              {suggestions.map((suggestion) => {
                const Icon = TYPE_ICONS[suggestion.type] || ListChecks;
                const isEditing = editingId === suggestion.id;
                const isProcessing = processingId === suggestion.id;
                const isPending = suggestion.status === 'pending';

                return (
                  <Card
                    key={suggestion.id}
                    className={`transition-all ${
                      !isPending ? 'opacity-60' : ''
                    } ${isEditing ? 'ring-2 ring-purple-500' : ''}`}
                  >
                    <CardContent className="p-4">
                      {isEditing ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Title / Description</Label>
                            <Input
                              value={editedData.title || ''}
                              onChange={(e) => setEditedData(prev => ({ ...prev, title: e.target.value }))}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Owner</Label>
                            <Input
                              value={editedData.owner_suggestion || ''}
                              onChange={(e) => setEditedData(prev => ({ ...prev, owner_suggestion: e.target.value }))}
                              className="mt-1"
                              placeholder="Assign owner..."
                            />
                          </div>
                          {suggestion.type === 'todo' && (
                            <div>
                              <Label className="text-xs">Due Date</Label>
                              <Input
                                type="date"
                                value={editedData.due_date || ''}
                                onChange={(e) => setEditedData(prev => ({ ...prev, due_date: e.target.value }))}
                                className="mt-1"
                              />
                            </div>
                          )}
                          <div className="flex gap-2 pt-2">
                            <Button size="sm" onClick={() => handleSaveEdit(suggestion)} disabled={isProcessing}>
                              {isProcessing ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Save className="h-4 w-4 mr-1" />
                              )}
                              Save & Apply
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="space-y-3">
                          {/* Header */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1">
                              <div className={`p-2 rounded-lg ${TYPE_COLORS[suggestion.type] || 'bg-gray-100'}`}>
                                <Icon className="h-4 w-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="outline" className="text-xs">
                                    {suggestion.type?.toUpperCase()}
                                  </Badge>
                                  <Badge variant="secondary" className="text-xs">
                                    {suggestion.action}
                                  </Badge>
                                  <Badge className={`${getConfidenceStyle(suggestion.confidence)} text-xs`}>
                                    {getConfidenceLabel(suggestion.confidence)} ({Math.round(suggestion.confidence * 100)}%)
                                  </Badge>
                                </div>
                                <h4 className="font-medium mt-1">{suggestion.title}</h4>
                              </div>
                            </div>

                            {/* Status Badge */}
                            {!isPending && (
                              <Badge
                                className={suggestion.status === 'approved' ? 'bg-green-600' : 'bg-gray-500'}
                              >
                                {suggestion.status === 'approved' ? 'Applied' : 'Rejected'}
                              </Badge>
                            )}
                          </div>

                          {/* Details */}
                          <div className="pl-11 space-y-2">
                            {/* What will change */}
                            <div className="text-sm">
                              <span className="text-muted-foreground">Change: </span>
                              <span>{suggestion.action === 'create' ? 'Create new' : 'Update existing'} {suggestion.type}</span>
                            </div>

                            {/* Where */}
                            {suggestion.target_id && (
                              <div className="text-sm">
                                <span className="text-muted-foreground">Target: </span>
                                <span>{suggestion.target_id}</span>
                              </div>
                            )}

                            {/* Owner */}
                            {suggestion.owner_suggestion && (
                              <div className="text-sm">
                                <span className="text-muted-foreground">Owner: </span>
                                <span className="font-medium">{suggestion.owner_suggestion}</span>
                              </div>
                            )}

                            {/* Evidence */}
                            {suggestion.evidence && (
                              <div className="text-xs p-2 bg-muted rounded-lg">
                                <span className="text-muted-foreground">Evidence: </span>
                                <span className="italic">&quot;{suggestion.evidence}&quot;</span>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          {isPending && (
                            <div className="flex items-center gap-2 pt-2 pl-11">
                              <Button
                                size="sm"
                                onClick={() => handleApprove(suggestion)}
                                disabled={isProcessing}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                {isProcessing ? (
                                  <Loader2 className="h-4 w-4" />
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEdit(suggestion)}
                                disabled={isProcessing}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleReject(suggestion)}
                                disabled={isProcessing}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="border-t pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mr-auto">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            No auto-apply. All changes require your approval.
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={onComplete}>
            Done Reviewing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewApplyDialog;
